import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Select, 
  Tag, 
  message, 
  Row, 
  Col,
  Space,
  Input as AntInput,
  Upload,
  Modal
} from 'antd';
import { 
  SaveOutlined, 
  EyeOutlined, 
  DeleteOutlined,
  UploadOutlined,
  PlusOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import TextArea from 'antd/es/input/TextArea';
import './index.css';

const { Option } = Select;

const WritePost = ({ user, onCreatePost, onUpdatePost, posts }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [imageList, setImageList] = useState([]);
  
  // 如果是编辑模式，加载数据
  useEffect(() => {
    if (id && posts) {
      const postToEdit = posts.find(post => post.id === parseInt(id));
      if (postToEdit) {
        form.setFieldsValue({
          title: postToEdit.title,
          content: postToEdit.content,
          category: postToEdit.category || '生活随笔'
        });
        setTags(postToEdit.tags || []);
        if (postToEdit.image) {
          setImageList([{
            uid: '-1',
            name: 'cover-image',
            status: 'done',
            url: postToEdit.image
          }]);
        }
      }
    }
  }, [id, posts, form]);

  // 处理标签
  const handleTagClose = (removedTag) => {
    const newTags = tags.filter(tag => tag !== removedTag);
    setTags(newTags);
  };

  const showInput = () => {
    setInputVisible(true);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputConfirm = () => {
    if (inputValue && !tags.includes(inputValue)) {
      setTags([...tags, inputValue]);
    }
    setInputVisible(false);
    setInputValue('');
  };

  // 图片上传
  const handleImageUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('只能上传 JPG/PNG 格式的图片！');
      return false;
    }
    
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('图片大小不能超过5MB！');
      return false;
    }

    // 转换为base64
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const newFile = {
          uid: file.uid,
          name: file.name,
          status: 'done',
          url: reader.result
        };
        setImageList([newFile]);
        resolve(newFile);
      };
    });
  };

  // 表单提交
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const postData = {
        ...values,
        tags: tags,
        image: imageList[0]?.url || null,
        excerpt: values.content.substring(0, 100) + '...'
      };

      if (id) {
        // 编辑模式
        await onUpdatePost(id, postData);
        message.success('文章更新成功！');
      } else {
        // 新建模式
        const newPost = await onCreatePost(postData);
        message.success('文章发布成功！');
        navigate(`/post/${newPost.id}`);
      }
      
      // 清空表单
      form.resetFields();
      setTags([]);
      setImageList([]);
    } catch (error) {
      message.error('操作失败，请重试！');
    } finally {
      setLoading(false);
    }
  };

  // 保存草稿
  const handleSaveDraft = () => {
    const values = form.getFieldsValue();
    const draftData = {
      ...values,
      tags: tags,
      image: imageList[0]?.url || null,
      draft: true,
      savedAt: new Date().toLocaleString()
    };
    
    localStorage.setItem('blog_draft', JSON.stringify(draftData));
    message.success('草稿已保存到本地');
  };

  // 加载草稿
  const handleLoadDraft = () => {
    const draftData = localStorage.getItem('blog_draft');
    if (draftData) {
      try {
        const parsed = JSON.parse(draftData);
        form.setFieldsValue({
          title: parsed.title || '',
          content: parsed.content || '',
          category: parsed.category || '生活随笔'
        });
        setTags(parsed.tags || []);
        if (parsed.image) {
          setImageList([{
            uid: '-1',
            name: 'draft-image',
            status: 'done',
            url: parsed.image
          }]);
        }
        message.success('已加载草稿');
      } catch (error) {
        message.error('加载草稿失败');
      }
    } else {
      message.info('没有找到草稿');
    }
  };

  // 删除草稿
  const handleDeleteDraft = () => {
    Modal.confirm({
      title: '确认删除草稿',
      content: '草稿删除后将无法恢复，确定要删除吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: () => {
        localStorage.removeItem('blog_draft');
        message.success('草稿已删除');
      }
    });
  };

  // 清空表单
  const handleClearForm = () => {
    Modal.confirm({
      title: '确认清空',
      content: '确定要清空当前表单内容吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: () => {
        form.resetFields();
        setTags([]);
        setImageList([]);
        message.success('表单已清空');
      }
    });
  };

  // 预览功能
  const handlePreview = () => {
    const values = form.getFieldsValue();
    if (!values.title || !values.content) {
      message.warning('请先填写标题和内容');
      return;
    }
    
    const previewData = {
      ...values,
      tags: tags,
      image: imageList[0]?.url,
      author: user?.username,
      date: new Date().toLocaleDateString()
    };
    
    // 这里可以跳转到预览页面，或者打开预览模态框
    message.info('预览功能开发中，请先发布文章查看效果');
  };

  return (
    <div className="write-post-page">
      <Card className="enhanced-card write-post-card border-glow">
        <div className="write-post-header">
          <h2>{id ? '编辑文章' : '写新文章'}</h2>
          <p>分享你的想法和经验</p>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="write-post-form"
        >
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                name="title"
                label="文章标题"
                rules={[{ required: true, message: '请输入文章标题！' }]}
              >
                <Input 
                  placeholder="请输入文章标题" 
                  size="large"
                  className="post-title-input jp-input"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="category"
                label="文章分类"
                rules={[{ required: true, message: '请选择文章分类！' }]}
              >
                <Select 
                  placeholder="选择分类" 
                  size="large"
                  className="jp-input"
                >
                  <Option value="生活随笔">生活随笔</Option>
                  <Option value="技术分享">技术分享</Option>
                  <Option value="读书心得">读书心得</Option>
                  <Option value="旅行见闻">旅行见闻</Option>
                  <Option value="美食分享">美食分享</Option>
                  <Option value="摄影作品">摄影作品</Option>
                  <Option value="设计思考">设计思考</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="文章标签">
                <div className="tags-container border-glow">
                  {tags.map((tag, index) => (
                    <Tag
                      key={tag + index}
                      closable
                      onClose={() => handleTagClose(tag)}
                      className="post-tag jp-tag"
                    >
                      {tag}
                    </Tag>
                  ))}
                  {inputVisible && (
                    <Input
                      type="text"
                      size="small"
                      className="tag-input jp-input"
                      value={inputValue}
                      onChange={handleInputChange}
                      onBlur={handleInputConfirm}
                      onPressEnter={handleInputConfirm}
                      placeholder="输入标签"
                      autoFocus
                    />
                  )}
                  {!inputVisible && (
                    <Tag 
                      className="site-tag-plus jp-tag"
                      onClick={showInput}
                    >
                      <PlusOutlined /> 添加标签
                    </Tag>
                  )}
                </div>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="content"
                label="文章内容"
                rules={[{ required: true, message: '请输入文章内容！' }]}
              >
                <TextArea 
                  placeholder="开始写作吧..."
                  rows={15}
                  className="post-content-textarea jp-input"
                  showCount
                  maxLength={10000}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="封面图片（可选）">
                <div className="upload-section border-glow">
                  <Upload
                    accept=".jpg,.jpeg,.png"
                    listType="picture-card"
                    fileList={imageList}
                    customRequest={({ file, onSuccess }) => {
                      handleImageUpload(file).then(() => {
                        onSuccess();
                      });
                    }}
                    onRemove={() => setImageList([])}
                    maxCount={1}
                  >
                    {imageList.length === 0 && (
                      <div className="upload-btn">
                        <UploadOutlined />
                        <div style={{ marginTop: 8 }}>上传封面</div>
                      </div>
                    )}
                  </Upload>
                  <p className="upload-hint">
                    支持 JPG/PNG 格式，建议尺寸 1200×600 像素
                  </p>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <div className="form-actions">
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  <Button 
                    icon={<SaveOutlined />}
                    onClick={handleSaveDraft}
                    className="draft-btn jp-btn"
                  >
                    保存草稿
                  </Button>
                  <Button 
                    icon={<EyeOutlined />}
                    onClick={handlePreview}
                    className="preview-btn jp-btn"
                  >
                    预览
                  </Button>
                  <Button 
                    icon={<ClearOutlined />}
                    onClick={handleClearForm}
                    className="clear-btn"
                  >
                    清空
                  </Button>
                </Space>
              </Col>
              
              <Col>
                <Space>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    loading={loading}
                    className="publish-btn jp-btn"
                    size="large"
                  >
                    {id ? '更新文章' : '发布文章'}
                  </Button>
                </Space>
              </Col>
            </Row>
            
            <div className="draft-management border-glow" style={{ marginTop: 16 }}>
              <Space>
                <Button 
                  size="small"
                  onClick={handleLoadDraft}
                  className="jp-btn"
                >
                  加载草稿
                </Button>
                <Button 
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDeleteDraft}
                >
                  删除草稿
                </Button>
              </Space>
            </div>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default WritePost;